import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { useEnableOpenApiV2IpAllowlist } from "@/features/openapi/useEnableOpenApiV2IpAllowlist";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { ApiKeyTable } from "@/pages/applications/pages/apiKeys/components/ApiKeyTable";
import { CreateApiKeyModal } from "@/pages/applications/pages/apiKeys/components/CreateApiKeyModal";
import { IpAllowlistModal } from "@/pages/applications/pages/apiKeys/components/IpAllowlistModal";
import { IpAllowlistSection } from "@/pages/applications/pages/apiKeys/components/IpAllowlistSection";
import { RenameApiKeyModal } from "@/pages/applications/pages/apiKeys/components/RenameApiKeyModal";
import { RevokeApiKeyModal } from "@/pages/applications/pages/apiKeys/components/RevokeApiKeyModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

type ApiKey = InterludeTypes["ApiKey"];
type SettableApiKeyStatus = InterludeTypes["SettableApiKeyStatus"];

const styles = {
  link: {
    textDecoration: "none",
  },
  pageTitle: {
    marginBottom: 0,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    width: "100%",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    width: "100%",
  },
  description: {
    flex: 1,
  },
  headerActions: {
    display: "flex",
    gap: "16px",
    flexShrink: 0,
    alignItems: "center",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const ApiKeysPageInternal: React.FC = function ApiKeysPageInternal() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [renamingApiKey, setRenamingApiKey] = useState<ApiKey | null>(null);
  const [revokingApiKey, setRevokingApiKey] = useState<ApiKey | null>(null);
  const [isIpAllowlistModalOpen, setIsIpAllowlistModalOpen] = useState(false);

  /**
   * The route already hides this page when the feature flag is off, but the
   * entitlement is per-organization and the URL is shareable, so the page has
   * to answer for it on its own too.
   */
  const organizationSettingQuery =
    interlude.organization.useSuspenseOrganizationSetting();
  const openApiV2Enabled = organizationSettingQuery.data.openApiV2Enabled;
  const ipAllowlistEnabled = useEnableOpenApiV2IpAllowlist();

  /**
   * Not requested at all for an organization without the entitlement: the
   * endpoint is gated on the same setting, so asking would only produce a
   * rejection behind the locked notice.
   */
  const apiKeysQuery = interlude.apiKey.useListApiKeys(undefined, {
    enabled: openApiV2Enabled,
  });

  /**
   * Gated on the same setting as the key list, for the same reason, and on the
   * release flag so a dark-shipped section does not call the endpoint.
   */
  const ipAllowlistQuery = interlude.apiKey.useGetIpAllowlist(undefined, {
    enabled: openApiV2Enabled && ipAllowlistEnabled,
  });

  /**
   * Wrapped rather than passed as `apiKeysQuery.refetch` directly: the click
   * handler is called with the mouse event, and `refetch` reads its first
   * argument as options.
   */
  const handleRetry = useHandler(() => {
    apiKeysQuery.refetch();
  });

  const handleContactUsClick = useHandler(() => {
    window.open(t("newRequest.link"), "_blank", "noopener,noreferrer");
  });

  const handleCreateClick = useHandler(() => {
    setIsCreateModalOpen(true);
  });

  const handleCloseCreateModal = useHandler(() => {
    setIsCreateModalOpen(false);
  });

  const handleCreateSuccess = useHandler(() => {
    enqueueSnackbar(t("apiKey.toast.createSuccess", "API key created."), {
      variant: "success",
    });
  });

  const handleRename = useHandler((apiKey: ApiKey) => {
    setRenamingApiKey(apiKey);
  });

  const handleCloseRenameModal = useHandler(() => {
    setRenamingApiKey(null);
  });

  const handleRenameSuccess = useHandler(() => {
    enqueueSnackbar(t("apiKey.toast.renameSuccess", "API key renamed."), {
      variant: "success",
    });
  });

  const handleRevoke = useHandler((apiKey: ApiKey) => {
    setRevokingApiKey(apiKey);
  });

  const handleCloseRevokeModal = useHandler(() => {
    setRevokingApiKey(null);
  });

  const handleRevokeSuccess = useHandler(() => {
    enqueueSnackbar(t("apiKey.toast.revokeSuccess", "API key revoked."), {
      variant: "success",
    });
  });

  const handleStatusUpdated = useHandler((status: SettableApiKeyStatus) => {
    enqueueSnackbar(
      status === "disabled"
        ? t("apiKey.toast.disableSuccess", "API key disabled.")
        : t("apiKey.toast.enableSuccess", "API key enabled."),
      { variant: "success" },
    );
  });

  const handleIpAllowlistRetry = useHandler(() => {
    ipAllowlistQuery.refetch();
  });

  const handleIpAllowlistEditClick = useHandler(() => {
    setIsIpAllowlistModalOpen(true);
  });

  const handleCloseIpAllowlistModal = useHandler(() => {
    setIsIpAllowlistModalOpen(false);
  });

  const handleIpAllowlistSaved = useHandler(() => {
    enqueueSnackbar(
      t(
        "apiKey.toast.ipAllowlistSaved",
        "IP allowlist saved. It takes effect within a few seconds.",
      ),
      { variant: "success" },
    );
  });

  return (
    <Stack gap="8px">
      <Breadcrumbs aria-label="breadcrumb" separator=">">
        <Box component={Link} to="/applications" sx={styles.link}>
          <Typography variant="body" color={theme.colors.staticFgBody}>
            {t("applications.title")}
          </Typography>
        </Box>
        <Typography variant="body" color={theme.colors.staticFgNote}>
          {t("apiKey.page.title", "API Key Management")}
        </Typography>
      </Breadcrumbs>
      <Box sx={styles.pageTitle}>
        <PageTitle>{t("apiKey.page.title", "API Key Management")}</PageTitle>
      </Box>

      <Box sx={styles.section}>
        <Box sx={styles.headerRow}>
          <Box sx={styles.description}>
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t(
                "apiKey.page.description",
                "Manage the API keys your systems use to call Crescendo Lab OpenAPI v2.",
              )}
            </Typography>
          </Box>
          <Box sx={styles.headerActions}>
            <ExternalLink
              href={t(
                "apiKey.page.docs.link",
                "https://doc.user360.cresclab.com/v2/",
              )}
              trailingIcon={true}
            >
              {t("apiKey.page.docs.label", "API documentation")}
            </ExternalLink>
            {openApiV2Enabled ? (
              <Button variant="primary" onClick={handleCreateClick}>
                {t("apiKey.page.createButton", "Create API key")}
              </Button>
            ) : null}
          </Box>
        </Box>

        {!openApiV2Enabled ? (
          <InfoBox
            variant="warning"
            title={t(
              "apiKey.page.notEntitled.title",
              "OpenAPI v2 is not enabled for this organization",
            )}
            description={t(
              "apiKey.page.notEntitled.description",
              "Contact us to enable OpenAPI v2 for your organization, then API keys can be managed here.",
            )}
            action={{
              label: t("common.contactUs"),
              onClick: handleContactUsClick,
            }}
          />
        ) : (
          <>
            <ApiKeyTable
              apiKeys={apiKeysQuery.data?.data ?? []}
              isLoading={apiKeysQuery.isPending}
              isError={apiKeysQuery.isError}
              onRename={handleRename}
              onRetry={handleRetry}
              onRevoke={handleRevoke}
              onStatusUpdated={handleStatusUpdated}
            />
            {ipAllowlistEnabled ? (
              <IpAllowlistSection
                ipAllowlist={
                  ipAllowlistQuery.isSuccess
                    ? ipAllowlistQuery.data.ipAllowlist
                    : undefined
                }
                isLoading={ipAllowlistQuery.isPending}
                isError={ipAllowlistQuery.isError}
                onEdit={handleIpAllowlistEditClick}
                onRetry={handleIpAllowlistRetry}
              />
            ) : null}
          </>
        )}
      </Box>

      {/*
       * No `refetch` or `invalidateQueries` anywhere in these flows: the zodios
       * wrapper invalidates the whole `apiKey` namespace on every mutation's
       * `onSettled`, so the list reloads on its own.
       */}
      <CreateApiKeyModal
        open={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />

      <RenameApiKeyModal
        open={!!renamingApiKey}
        apiKey={renamingApiKey}
        onClose={handleCloseRenameModal}
        onSuccess={handleRenameSuccess}
      />

      <RevokeApiKeyModal
        open={!!revokingApiKey}
        apiKey={revokingApiKey}
        onClose={handleCloseRevokeModal}
        onSuccess={handleRevokeSuccess}
      />

      {ipAllowlistEnabled ? (
        <IpAllowlistModal
          open={isIpAllowlistModalOpen}
          ipAllowlist={ipAllowlistQuery.data?.ipAllowlist}
          onClose={handleCloseIpAllowlistModal}
          onSuccess={handleIpAllowlistSaved}
        />
      ) : null}
    </Stack>
  );
};

export const ApiKeysPage: React.FC = function ApiKeysPage() {
  return (
    <PageLayout>
      <Suspense fallback={<BarLoading />}>
        <ApiKeysPageInternal />
      </Suspense>
    </PageLayout>
  );
};
